/**
 * Event chip for month view calendar.
 * Shows a compact event with colored left border, time, and truncated title.
 */

import {
  allNonOrganizerGuestsDeclined,
  DECLINED_TITLE_CLASSNAME,
} from '@/lib/crm/record/utils/meetingHelpers';
import { EntityTypeIcon } from '@/lib/crm/utils/icons/EntityTypeIcon';
import { useOrgTimeFormat } from '@/lib/time';
import { cx } from '@/utils/cx';

import {
  getEventColor,
  mapCalendarColorToScheduleColor,
} from '../utils/calendarColors';
import type { EventColor } from '../utils/calendarUtils';
import { getPastEventStyles } from '../utils/calendarUtils';
import { formatEventTime } from '../utils/dateUtils';
import type { CalendarEvent } from '../utils/eventMapper';

interface MonthEventChipProps {
  event: CalendarEvent;
  /** IANA timezone (profile timezone) the event's clock time is read in */
  timezone: string;
  onClick?: (event: CalendarEvent) => void;
  /** Whether this event spans multiple days */
  isMultiDay?: boolean;
  /** Position in multi-day span: 'start', 'middle', 'end', or undefined for single-day */
  spanPosition?: 'start' | 'middle' | 'end';
  /** Whether this event is selected (shows ring styling) */
  isSelected?: boolean;
  /** Whether this event is in the past (faded appearance) */
  isPast?: boolean;
}

/**
 * MonthEventChip - Compact event display for month grid.
 *
 * Displays:
 * - Colored left border (from Google Calendar color)
 * - Event time (start time)
 * - Truncated title
 */
export function MonthEventChip({
  event,
  timezone,
  onClick,
  isMultiDay,
  spanPosition,
  isSelected,
  isPast,
}: MonthEventChipProps) {
  const { timeFormat } = useOrgTimeFormat();
  const color = getEventColor(event.color);
  // De-emphasize meetings where every non-organizer guest declined.
  // See WeekDayColumn for the rationale — calendar shows them but
  // strikes through to signal they're effectively dead.
  const isAllDeclined = allNonOrganizerGuestsDeclined(event.guests);
  // All-day events have no clock time — `start_time` is a placeholder
  // midnight that's meaningless to display anywhere (rendered chip
  // text AND the DOM `title` hover tooltip).
  const time = event.is_all_day
    ? ''
    : formatEventTime(event.start_time, timeFormat, timezone);

  // Get styling from utility function
  const eventStyles = getPastEventStyles(
    // Map Google Calendar color to EventColor type
    mapCalendarColorToScheduleColor(event.color) as EventColor,
    isPast || false,
    color
  );

  // All-day events have no meaningful clock time — their start_time is just
  // <startDate>T00:00:00. Suppressing the time keeps the chip focused on
  // the title for both single-day and multi-day all-day events.
  // For TIMED multi-day events, the time only renders on the start day.
  const showTime =
    !event.is_all_day && (!isMultiDay || spanPosition === 'start');

  // Determine border radius based on span position
  const borderRadiusClass = isMultiDay
    ? cx(
        spanPosition === 'start' && 'rounded-l',
        spanPosition === 'end' && 'rounded-r',
        spanPosition === 'middle' && 'rounded-none'
      )
    : 'rounded';

  // Draft "ghost" task (calendar preview while composing): dashed,
  // translucent, non-interactive — distinct from a saved all-day chip.
  if (event.isDraft) {
    return (
      <div
        className={cx(
          'flex w-full items-center gap-1 px-1.5 py-0.5 text-left text-xs',
          'border border-dashed border-[var(--color-bg-brand-solid)] bg-[var(--color-bg-brand-solid)]/10',
          'pointer-events-none overflow-hidden',
          borderRadiusClass
        )}
        title={event.title}
      >
        <span className='truncate flex-1 font-medium text-[var(--color-bg-brand-solid)]'>
          {event.title}
        </span>
      </div>
    );
  }

  return (
    <button
      type='button'
      onClick={e => {
        e.stopPropagation();
        onClick?.(event);
      }}
      className={cx(
        'group flex w-full items-center gap-1 px-1.5 py-0.5 text-left text-xs',
        isPast
          ? 'bg-tertiary hover:bg-tertiary'
          : 'bg-secondary hover:bg-tertiary',
        'transition-colors cursor-pointer',
        'overflow-hidden',
        borderRadiusClass,
        !isSelected && 'ring-1 ring-black/10 dark:ring-white/10',
        isSelected && 'ring-2 ring-[var(--color-focus-ring)]'
      )}
      style={{
        // Use inline styles from utility function (includes color-mix + hex alpha fallback)
        borderLeft: eventStyles.inlineBorderLeft,
        backgroundColor: eventStyles.inlineBackgroundColor,
        ...eventStyles.inlineStyles,
      }}
      title={`${event.title}${time ? ` - ${time}` : ''}`}
    >
      {/* Continuation indicator on the LEFT when this chip is the
          right-hand slice of a multi-day event (started before this
          day or earlier in the row). Visual cue: the cut edge has a
          `‹` chevron flush against it. */}
      {(spanPosition === 'end' || spanPosition === 'middle') && (
        <span
          aria-hidden
          className={cx(
            'shrink-0 leading-none -ml-0.5',
            isPast ? 'text-quaternary' : 'text-tertiary'
          )}
        >
          ‹
        </span>
      )}
      {showTime && time && (
        <span
          className={cx(
            'flex-shrink-0 font-medium',
            isPast ? 'text-quaternary' : 'text-tertiary'
          )}
        >
          {time}
        </span>
      )}
      {/* Entity-type icon for non-meeting kinds (notes, custom event types).
          Meetings are the default calendar entity — an icon there would
          just add noise. */}
      {event.entity_type_name !== 'ahoy_meeting' && (
        <EntityTypeIcon
          entityTypeName={event.entity_type_name}
          className='size-3 shrink-0'
        />
      )}
      <span
        className={cx(
          'truncate flex-1',
          isPast ? 'text-tertiary' : 'text-secondary',
          isAllDeclined && DECLINED_TITLE_CLASSNAME
        )}
      >
        {event.title}
        {isPast ? ' (Past)' : ''}
      </span>
      {/* Continuation indicator on the RIGHT when this chip is the
          left-hand slice of a multi-day event (extends into the next
          day or later in the row). Visual cue: the cut edge has a
          `›` chevron flush against it. */}
      {(spanPosition === 'start' || spanPosition === 'middle') && (
        <span
          aria-hidden
          className={cx(
            'shrink-0 leading-none -mr-0.5',
            isPast ? 'text-quaternary' : 'text-tertiary'
          )}
        >
          ›
        </span>
      )}
    </button>
  );
}
